import { useEffect, useState } from "react";
import { Loader2, User, Mail, Bookmark, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/admin/members")
      .then((r) => setMembers(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="members-section">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-black tracking-tight text-[#1F2547]">
          Members
        </h1>
        <p className="text-sm text-[#5B6079] font-medium mt-1">
          Visitors who signed up with Google. Read-only for now.
        </p>
      </header>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
            Total members
          </div>
          <div className="font-display text-3xl font-black text-[#1F2547] mt-1">
            {loading ? "—" : members.length}
          </div>
        </div>
        <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
            Free tier
          </div>
          <div className="font-display text-3xl font-black text-[#1F2547] mt-1">
            {loading ? "—" : members.filter((m) => (m.tier || "free") === "free").length}
          </div>
        </div>
        <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
            Paid tier
          </div>
          <div className="font-display text-3xl font-black text-[#9F87D8] mt-1">
            {loading ? "—" : members.filter((m) => m.tier === "paid").length}
          </div>
        </div>
        <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079]">
            Total downloads
          </div>
          <div className="font-display text-3xl font-black text-[#1F2547] mt-1">
            {loading ? "—" : members.reduce((s, m) => s + (m.downloads_count || 0), 0)}
          </div>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium mb-4">
          {err}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[#5B6079] font-medium">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl bg-white brutal-shadow-sm p-6 text-[#5B6079] font-medium">
          No members yet — as visitors sign in with Google, they&apos;ll appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((m) => (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="bg-white rounded-2xl brutal-shadow-sm p-5 flex items-center gap-4"
            >
              {m.picture ? (
                <img
                  src={m.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#9F87D8] text-white flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="font-display font-bold text-base text-[#1F2547]">
                    {m.name || m.email}
                  </h3>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${m.tier === "paid" ? "bg-[#9F87D8] text-white" : "bg-[#D4EDDB] text-[#1F2547]"}`}
                  >
                    {m.tier || "free"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5B6079] font-medium">
                  <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>
                  <span className="inline-flex items-center gap-1"><Bookmark className="w-3 h-3" /> {m.bookmarks_count || 0}</span>
                  <span className="inline-flex items-center gap-1"><Download className="w-3 h-3" /> {m.downloads_count || 0}</span>
                  <span>
                    Joined {new Date(m.created_at).toLocaleDateString()} · last seen {m.last_login_at ? new Date(m.last_login_at).toLocaleDateString() : "—"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
